<script setup>
import { useUsersStore } from "@/stores"
import { phoneValidator, requiredValidator, urlValidator } from "@validators"
import axios from "@axios"
import moment from 'moment'
import { loadRmStripe } from '@/utils/stripe'
import { computed, nextTick, ref, watch } from "vue"
import { storeToRefs } from "pinia"
import PreferredRequestInfoDialog from '@core/components/preferredRequestInfoDialog.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  productId: { type: String, default: null },
  priceDollars: { type: Number, default: null },
  message: { type: String, default: null },
  isExtension: { type: Boolean, default: false },
})

const emit = defineEmits(['complete', 'cancel'])

const usersStore = useUsersStore()
const { myUser, isShowingPreferredRequestInfoDialog } = storeToRefs(usersStore)

const refVForm = ref()
const currentStep = ref(1)
const totalSteps = 4
const saving = ref(false)

// Payment state (step 4)
const stripeInstance = ref(null)
const stripeElements = ref(null)
const cardElement = ref(null)
const cardElementMounted = ref(false)
const cardElementContainerRef = ref(null)
const paymentMethods = ref([])
const selectedPaymentMethod = ref(null)
const showNewCard = ref(false)
const newCardComplete = ref(false)
const processingPayment = ref(false)
const paymentError = ref('')
const saveCardForFuture = ref(true)
const clientSecret = ref(null)

// Step 1: About You
const firstName = ref('')
const lastName = ref('')
const mobilePhoneNumber = ref('')
const company = ref('')
const title = ref('')

// Step 2: Additional Details
const clubsGroupsAffiliation = ref('')
const clubsGroups = ref('')
const restaurantAffiliation = ref('')
const restaurantAffiliationNames = ref('')
const diningFrequency = ref('')

// Step 3: Social Links
const bioPage = ref('')
const linkedInProfile = ref('')
const instagramProfile = ref('')
const xProfile = ref('')
const reviewedRM = ref(true)

// Pre-fill from user profile when dialog opens
watch(() => props.modelValue, async isOpen => {
  if (isOpen && myUser.value) {
    const u = myUser.value
    firstName.value = u.firstName || ''
    lastName.value = u.lastName || ''
    mobilePhoneNumber.value = u.phoneNumber || ''
    company.value = u.company || ''
    title.value = u.title || ''
    clubsGroupsAffiliation.value = u.clubsGroupsAffiliation || ''
    clubsGroups.value = u.clubsGroups || ''
    restaurantAffiliation.value = u.restaurantAffiliation || ''
    restaurantAffiliationNames.value = u.restaurantAffiliationNames || ''
    diningFrequency.value = u.diningFrequency || ''
    bioPage.value = u.profileWebsiteLink || ''
    linkedInProfile.value = u.profileLinkedInProfileId || ''
    instagramProfile.value = u.profileInstagramProfileId || ''
    xProfile.value = u.profileXProfileId || ''
    reviewedRM.value = true

    if (props.isExtension) {
      // Existing Platinum members skip the profile wizard and go straight to payment
      currentStep.value = 4
      await nextTick()
      initializePaymentStep()
    } else {
      currentStep.value = 1
    }
  }
}, { immediate: true })

// End-of-extension date to show on the payment step (e.g., "Extends through M/D/YY")
const extensionEndsOnString = computed(() => {
  if (!props.isExtension || !myUser.value?.platinumRenewalDate) return null

  return moment(myUser.value.platinumRenewalDate).subtract(1, 'day').add(1, 'year').format('M/D/YY')
})

// Navigation
const goToNextStep = async () => {
  const { valid: isValid } = await refVForm.value?.validate()
  if (!isValid) return

  // When leaving step 3 (social links), save profile and init payment
  if (currentStep.value === 3) {
    saving.value = true
    try {
      myUser.value.firstName = firstName.value
      myUser.value.lastName = lastName.value
      myUser.value.phoneNumber = mobilePhoneNumber.value
      myUser.value.company = company.value
      myUser.value.title = title.value
      myUser.value.clubsGroupsAffiliation = clubsGroupsAffiliation.value
      myUser.value.clubsGroups = clubsGroups.value
      myUser.value.restaurantAffiliation = restaurantAffiliation.value
      myUser.value.restaurantAffiliationNames = restaurantAffiliationNames.value
      myUser.value.diningFrequency = diningFrequency.value
      myUser.value.profileWebsiteLink = bioPage.value
      myUser.value.profileLinkedInProfileId = linkedInProfile.value
      myUser.value.profileInstagramProfileId = instagramProfile.value
      myUser.value.profileXProfileId = xProfile.value

      await usersStore.updateMyUser()
    } catch (e) {
      console.error('Failed to save profile:', e)
      saving.value = false
      
      return
    }
    saving.value = false

    // Initialize payment for step 4
    currentStep.value++
    initializePaymentStep()
    
    return
  }

  currentStep.value++
}

const goToPrevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

// ========== Payment (Step 5) ==========

const initializeStripe = async () => {
  if (stripeInstance.value) return
  // RM-313: the key comes from the server that will process the card.
  try {
    stripeInstance.value = await loadRmStripe()
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
  }
  if (!stripeInstance.value) {
    paymentError.value = 'Failed to load payment form. Please refresh the page.'
    
    return
  }
  stripeElements.value = stripeInstance.value.elements()
}

const loadPaymentMethods = async () => {
  try {
    const res = await axios.get('/checkout/payment-methods', {
      params: {}
    })
    paymentMethods.value = res.data
    if (paymentMethods.value.length === 0) {
      showNewCard.value = true
      await mountCardElement()
    } else {
      selectedPaymentMethod.value = paymentMethods.value[0].id
      showNewCard.value = false
    }
  } catch (error) {
    console.error('Failed to load payment methods:', error)
    showNewCard.value = true
    await mountCardElement()
  }
}

const mountCardElement = async () => {
  if (cardElement.value) {
    try { cardElement.value.unmount() } catch (e) { /* ignore */ }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
  }
  await initializeStripe()
  if (!stripeElements.value) return

  cardElement.value = stripeElements.value.create('card', {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': { color: '#aab7c4' },
      },
      invalid: { color: '#9e2146' },
    },
  })

  cardElement.value.on('change', event => {
    newCardComplete.value = event.complete
    paymentError.value = event.error ? event.error.message : ''
  })

  await nextTick()
  if (cardElementContainerRef.value && !cardElementMounted.value) {
    cardElement.value.mount(cardElementContainerRef.value)
    cardElementMounted.value = true
  }
}

watch(cardElementContainerRef, el => {
  if (el && cardElement.value && !cardElementMounted.value) {
    cardElement.value.mount(el)
    cardElementMounted.value = true
  }
})

const unmountCardElement = () => {
  if (cardElement.value) {
    try { cardElement.value.unmount() } catch (e) { /* ignore */ }
    cardElement.value.destroy()
    cardElement.value = null
    cardElementMounted.value = false
    newCardComplete.value = false
  }
}

const handleAddNewCard = async () => {
  showNewCard.value = true
  selectedPaymentMethod.value = null
  newCardComplete.value = false
  await mountCardElement()
}

const handleSelectSavedCard = () => {
  unmountCardElement()
  showNewCard.value = false
  if (paymentMethods.value.length > 0 && !selectedPaymentMethod.value) {
    selectedPaymentMethod.value = paymentMethods.value[0].id
  }
}

const isPaymentReady = computed(() => {
  return selectedPaymentMethod.value || (showNewCard.value && newCardComplete.value)
})

const createPaymentIntent = async () => {
  const res = await axios.post('/checkout/create-membership-payment-intent', {
    planId: props.productId,
    priceDollars: props.priceDollars,
  }, {
    params: {}
  })
  clientSecret.value = res.data.clientSecret
}

const initializePaymentStep = async () => {
  paymentError.value = ''
  processingPayment.value = false
  try {
    await Promise.all([
      initializeStripe(),
      createPaymentIntent(),
    ])
    await loadPaymentMethods()
  } catch (error) {
    console.error('Failed to initialize payment step:', error)
    paymentError.value = 'Failed to load payment form. Please try again.'
  }
}

// Submit payment
const onSubmitPayment = async () => {
  if (!isPaymentReady.value || !clientSecret.value) return

  processingPayment.value = true
  paymentError.value = ''

  try {
    let paymentMethodId

    if (showNewCard.value && cardElement.value) {
      // Create payment method from new card
      const { paymentMethod, error } = await stripeInstance.value.createPaymentMethod({
        type: 'card',
        card: cardElement.value,
      })
      if (error) {
        paymentError.value = error.message
        processingPayment.value = false
        
        return
      }
      paymentMethodId = paymentMethod.id
    } else {
      paymentMethodId = selectedPaymentMethod.value
    }

    // Confirm the PaymentIntent
    const { paymentIntent, error: confirmError } = await stripeInstance.value.confirmCardPayment(clientSecret.value, {
      payment_method: paymentMethodId,
    })

    if (confirmError) {
      paymentError.value = confirmError.message
      processingPayment.value = false
      
      return
    }

    // Process the subscription on the backend — extensions create a subscription
    // directly; new requests go through admin approval first.
    const confirmEndpoint = props.isExtension
      ? '/checkout/confirm-membership-extension'
      : '/checkout/confirm-membership-payment'

    await axios.post(confirmEndpoint, {
      paymentIntentId: paymentIntent.id,
      planId: props.productId,
      priceDollars: props.priceDollars,
      message: props.message,
      saveCard: saveCardForFuture.value && showNewCard.value,
      paymentMethodId,
    }, {
      params: {}
    })

    // Success - refresh user data and close
    await usersStore.getMyUser()
    unmountCardElement()
    emit('complete')
  } catch (error) {
    console.error('Payment failed:', error)
    paymentError.value = error.response?.data?.error || 'Payment failed. Please try again.'
  } finally {
    processingPayment.value = false
  }
}

const onClose = () => {
  unmountCardElement()
  emit('cancel')
}

// Phone formatting
const formatPhoneNumber = () => {
  if (!mobilePhoneNumber.value) return
  let digits = mobilePhoneNumber.value.replace(/\D/g, '')
  if (digits.length > 10 && digits.startsWith('1')) {
    digits = digits.substring(1)
  }
  digits = digits.substring(0, 10)
  if (digits.length === 0) {
    mobilePhoneNumber.value = ''
  } else if (digits.length <= 3) {
    mobilePhoneNumber.value = `(${digits}`
  } else if (digits.length <= 6) {
    mobilePhoneNumber.value = `(${digits.substring(0, 3)}) ${digits.substring(3)}`
  } else {
    mobilePhoneNumber.value = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
  }
}

// URL normalizers
const normalizeLinkedInUrl = () => {
  if (linkedInProfile.value && linkedInProfile.value.trim()) {
    let value = linkedInProfile.value.trim()
    const prefixes = [
      'https://www.linkedin.com/in/',
      'http://www.linkedin.com/in/',
      'https://linkedin.com/in/',
      'http://linkedin.com/in/',
      'www.linkedin.com/in/',
      'linkedin.com/in/'
    ]
    for (const prefix of prefixes) {
      if (value.toLowerCase().startsWith(prefix.toLowerCase())) {
        value = value.substring(prefix.length)
        break
      }
    }
    value = value.replace(/\/+$/, '').replace(/^\/+/, '')
    linkedInProfile.value = value
  }
}

const normalizeInstagramUrl = () => {
  if (instagramProfile.value && instagramProfile.value.trim()) {
    const trimmed = instagramProfile.value.trim()
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      if (trimmed.startsWith('www.instagram.com/') || trimmed.startsWith('instagram.com/')) {
        instagramProfile.value = 'https://' + (trimmed.startsWith('www.') ? trimmed : 'www.' + trimmed)
      } else {
        const username = trimmed.startsWith('@') ? trimmed.substring(1) : trimmed
        instagramProfile.value = 'https://www.instagram.com/' + username
      }
    } else if (trimmed.startsWith('http://')) {
      instagramProfile.value = trimmed.replace('http://', 'https://')
    }
  }
}

const normalizeXUrl = () => {
  if (xProfile.value && xProfile.value.trim()) {
    let trimmed = xProfile.value.trim()
    if (trimmed.includes('twitter.com')) {
      trimmed = trimmed.replace('twitter.com', 'x.com')
    }
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
      if (trimmed.startsWith('x.com/')) {
        xProfile.value = 'https://' + trimmed
      } else {
        const username = trimmed.startsWith('@') ? trimmed.substring(1) : trimmed
        xProfile.value = 'https://x.com/' + username
      }
    } else if (trimmed.startsWith('http://')) {
      xProfile.value = trimmed.replace('http://', 'https://')
    } else {
      xProfile.value = trimmed
    }
  }
}
</script>

<template>
  <PreferredRequestInfoDialog />

  <VDialog
    :model-value="modelValue"
    fullscreen
    persistent
  >
    <VCard
      class="text-center px-5 pt-4 pb-0"
      style="position: relative; overflow-y: auto;"
    >
      <VBtn
        v-if="currentStep > 1 && !isExtension"
        icon
        variant="text"
        size="small"
        color="secondary"
        style="position: absolute; top: 8px; left: 8px; z-index: 10;"
        @click="goToPrevStep"
      >
        <VIcon icon="mdi-arrow-left" />
      </VBtn>

      <VBtn
        icon
        variant="text"
        size="small"
        color="secondary"
        style="position: absolute; top: 8px; right: 8px; z-index: 10;"
        @click="onClose"
      >
        <VIcon icon="mdi-close" />
      </VBtn>

      <div style="max-width: 500px; margin: 0 auto; padding-top: 20px; width: 100%;">
        <!-- Step indicator (hidden for extensions since they jump straight to payment) -->
        <div
          v-if="!isExtension"
          class="wizard-step-indicator mb-6"
        >
          <div class="wizard-step-dots">
            <div
              v-for="step in totalSteps"
              :key="step"
              class="wizard-step-dot"
              :class="{
                active: step === currentStep,
                completed: step < currentStep
              }"
            />
          </div>
        </div>

        <VForm
          ref="refVForm"
          @submit.prevent
        >
          <!-- ===== STEP 1: About You ===== -->
          <div v-if="currentStep === 1">
            <div class="text-center mb-4">
              <h6 class="wizard-step-title">
                Request Platinum
              </h6>
              <p class="wizard-step-subtitle mt-2">
                Please confirm your details
              </p>
            </div>

            <VTextField
              v-model="firstName"
              autocomplete="off"
              :rules="[requiredValidator]"
              label="First Name"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextField
              v-model="lastName"
              autocomplete="off"
              :rules="[requiredValidator]"
              label="Last Name"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextField
              v-model="mobilePhoneNumber"
              autocomplete="off"
              :rules="[requiredValidator, phoneValidator]"
              label="Mobile Phone Number"
              type="tel"
              placeholder="(555) 123-4567"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
              @input="formatPhoneNumber"
            />
            <VTextField
              v-model="company"
              autocomplete="off"
              :rules="[requiredValidator]"
              label="Company"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextField
              v-model="title"
              autocomplete="off"
              :rules="[requiredValidator]"
              label="Position/Title"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />

            <VBtn
              class="mt-4"
              block
              color="primary"
              size="large"
              rounded="lg"
              @click.prevent="goToNextStep"
            >
              Next
            </VBtn>
          </div>

          <!-- ===== STEP 2: Additional Details ===== -->
          <div v-if="currentStep === 2">
            <div class="text-center mb-4">
              <h6 class="wizard-step-title">
                Additional Details
              </h6>
            </div>

            <VSelect
              v-model="clubsGroupsAffiliation"
              :items="['Yes', 'No']"
              :rules="[requiredValidator]"
              :menu-props="{ location: 'bottom' }"
              label="Are you a member of any high profile groups?"
              hint="e.g. NFLPA, YPO, Triumph Jets, Golf/Social Clubs"
              persistent-hint
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextarea
              v-if="clubsGroupsAffiliation === 'Yes'"
              v-model="clubsGroups"
              autocomplete="off"
              :rules="[requiredValidator]"
              rows="3"
              label="Please share group name(s)"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />

            <VSelect
              v-model="restaurantAffiliation"
              :items="['Yes', 'No']"
              :menu-props="{ location: 'bottom' }"
              hint="e.g. Investor, Executive/Partner, VIP/Club Member"
              persistent-hint
              :rules="[requiredValidator]"
              label="Are you affiliated with any restaurants?"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextarea
              v-if="restaurantAffiliation && restaurantAffiliation.startsWith('Yes')"
              v-model="restaurantAffiliationNames"
              autocomplete="off"
              :rules="[requiredValidator]"
              rows="2"
              label="Please share restaurant name(s)"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />

            <VSelect
              v-model="diningFrequency"
              :items="[
                '1-2x per week',
                '1-2x per month',
                '1-2x per quarter',
                '1-2x per year'
              ]"
              :rules="[requiredValidator]"
              label="How often do you dine at popular restaurants?"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />

            <VBtn
              class="mt-4"
              block
              color="primary"
              size="large"
              rounded="lg"
              @click.prevent="goToNextStep"
            >
              Next
            </VBtn>
          </div>

          <!-- ===== STEP 3: Social Links ===== -->
          <div v-if="currentStep === 3">
            <div class="text-center mb-4">
              <h6 class="wizard-step-title">
                Social Links
              </h6>
              <p class="wizard-step-subtitle mt-2">
                Optional: these help us verify identity & expedite review
              </p>
            </div>

            <VTextField
              v-model="bioPage"
              autocomplete="off"
              :rules="[v => !v || urlValidator(v)]"
              label="Bio page (company/personal)"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
            />
            <VTextField
              v-model="linkedInProfile"
              autocomplete="off"
              label="LinkedIn profile"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
              @blur="normalizeLinkedInUrl"
            />
            <VTextField
              v-model="instagramProfile"
              autocomplete="off"
              :rules="[v => !v || urlValidator(v)]"
              label="Instagram profile"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
              @blur="normalizeInstagramUrl"
            />
            <VTextField
              v-model="xProfile"
              autocomplete="off"
              :rules="[v => !v || urlValidator(v)]"
              label="X profile"
              type="text"
              variant="solo-filled"
              flat
              rounded="lg"
              bg-color="grey-lighten-4"
              density="comfortable"
              class="mb-3"
              @blur="normalizeXUrl"
            />

            <div class="d-flex align-center mt-3">
              <VCheckbox
                id="upgrade-reviewed-rm"
                v-model="reviewedRM"
                inline
                :rules="[requiredValidator]"
                hide-details
                style="margin-top: -4px;"
              />
              <VLabel
                for="upgrade-reviewed-rm"
                style="opacity: 1; white-space: normal; flex: 1;"
              >
                <span class="me-1">I understand <a
                  class="text-primary cursor-pointer"
                  @click.stop.prevent="isShowingPreferredRequestInfoDialog = true"
                >how RM works</a></span>
              </VLabel>
            </div>

            <VBtn
              class="mt-6"
              block
              color="primary"
              size="large"
              rounded="lg"
              :loading="saving"
              @click.prevent="goToNextStep"
            >
              Next
            </VBtn>
          </div>

          <!-- ===== STEP 4: Payment ===== -->
          <div v-if="currentStep === 4">
            <div class="text-center mb-4">
              <h6 class="wizard-step-title">
                {{ isExtension ? 'Extend Platinum' : 'Platinum Membership' }}
              </h6>
              <p class="wizard-step-subtitle mt-2">
                ${{ priceDollars }}/year
              </p>
              <p
                v-if="isExtension && extensionEndsOnString"
                class="wizard-step-subtitle mt-1"
              >
                Extends through {{ extensionEndsOnString }}
              </p>
            </div>

            <!-- How it Works Section -->
            <details class="payment-info-section mt-4">
              <summary
                class="payment-info-header"
                style="cursor: pointer; list-style: none;"
              >
                <VIcon
                  icon="mdi-shield-check-outline"
                  size="22"
                  color="primary-dark"
                />
                <span class="payment-info-title">How it works</span>
                <span style="margin-left: auto; font-size: 0.7rem; color: #666;">&#9656;</span>
              </summary>
              <div
                class="payment-info-body"
                style="margin-top: 12px;"
              >
                <template v-if="isExtension">
                  <p class="payment-info-note">
                    <span class="payment-info-amount">${{ priceDollars }}</span> temporary credit card hold with RM for your Platinum annual renewal.
                  </p>
                  <p
                    class="payment-info-note"
                    style="margin-top: 8px;"
                  >
                    Your hold will be captured to extend your membership <template v-if="extensionEndsOnString">
                      through {{ extensionEndsOnString }}
                    </template>.
                  </p>
                </template>
                <template v-else>
                  <p class="payment-info-note">
                    <span class="payment-info-amount">${{ priceDollars }}</span> temporary credit card hold with RM. If you are not approved for Platinum we will release your credit card hold in full.
                  </p>
                  <p
                    class="payment-info-note"
                    style="margin-top: 8px;"
                  >
                    If accepted, your card will be charged for the full hold for the Platinum annual membership fee (<span class="payment-info-amount">${{ priceDollars }}</span>).
                  </p>
                </template>
              </div>
            </details>

            <!-- Credit Card Section -->
            <div class="credit-card-hold-section mt-4">
              <div class="credit-card-hold-header">
                <VIcon
                  icon="mdi-credit-card-outline"
                  size="20"
                />
                <span class="credit-card-hold-title">Credit Card</span>
              </div>
              <div class="credit-card-hold-body">
                <!-- Saved Cards Selector -->
                <VSelect
                  v-if="paymentMethods.length > 0 && !showNewCard"
                  v-model="selectedPaymentMethod"
                  :items="paymentMethods.map(pm => ({
                    title: `${pm.card.brand.toUpperCase()} •••• ${pm.card.last4}`,
                    value: pm.id
                  }))"
                  label="Select a card"
                  hide-details
                >
                  <template #prepend-item>
                    <VListItem @click="handleAddNewCard">
                      <template #prepend>
                        <VIcon icon="mdi-plus" />
                      </template>
                      <VListItemTitle>Add New Card</VListItemTitle>
                    </VListItem>
                    <VDivider class="my-2" />
                  </template>
                </VSelect>

                <!-- New Card Form -->
                <div v-if="showNewCard || paymentMethods.length === 0">
                  <div
                    ref="cardElementContainerRef"
                    class="card-element-container"
                  />
                  <VCheckbox
                    v-model="saveCardForFuture"
                    label="Save card for future use"
                    density="compact"
                    hide-details
                    class="mt-2"
                  />
                  <VBtn
                    v-if="paymentMethods.length > 0"
                    variant="text"
                    color="primary"
                    size="small"
                    class="mt-2"
                    @click="handleSelectSavedCard"
                  >
                    Use Saved Card
                  </VBtn>
                </div>
              </div>
            </div>

            <div
              v-if="paymentError"
              class="text-error text-sm mt-2"
            >
              {{ paymentError }}
            </div>

            <VBtn
              class="mt-6"
              block
              color="primary"
              size="large"
              rounded="lg"
              :loading="processingPayment"
              :disabled="!isPaymentReady"
              @click.prevent="onSubmitPayment"
            >
              {{ isExtension ? 'Extend Platinum' : 'Submit Request for Platinum' }}
            </VBtn>
            <p
              class="mt-4 text-center"
              style="font-size: 0.85rem; color: #888;"
            >
              Questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
            </p>
          </div>
        </VForm>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.wizard-step-indicator {
  display: flex;
  justify-content: center;
}

.wizard-step-dots {
  display: flex;
  gap: 8px;
}

.wizard-step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E0E0E0;
  transition: all 0.3s ease;

  &.active {
    background-color: var(--ds-brand-primary-dark);
    width: 24px;
    border-radius: 4px;
  }

  &.completed {
    background-color: var(--ds-brand-primary-dark);
  }
}

.wizard-step-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.wizard-step-subtitle {
  font-size: 0.9rem;
  color: var(--ds-text-muted);
  line-height: 1.5;
}

// Payment section styles
.payment-info-section {
  background: linear-gradient(135deg, #f8fafa 0%, #f0f7f6 100%);
  border: 1px solid #e0ebe9;
  border-radius: 12px;
  padding: 20px;
  text-align: left;
}

.payment-info-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.payment-info-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.payment-info-amount {
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ds-brand-primary-dark);
}

.payment-info-desc {
  font-size: 0.9rem;
  color: var(--ds-text-secondary);
}

.payment-info-note {
  font-size: 0.95rem;
  color: var(--ds-text-secondary);
  line-height: 1.5;
  margin: 0;
}

.credit-card-hold-section {
  background: var(--ds-surface-card);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid var(--ds-border-default);
  text-align: left;
}

.credit-card-hold-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.credit-card-hold-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--ds-text-strong);
}

.credit-card-hold-body {
  width: 100%;
}

.card-element-container {
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  padding: 14px;
  background: var(--ds-surface-card);

  &:focus-within {
    border-color: var(--ds-brand-primary-dark);
    box-shadow: 0 0 0 1px var(--ds-brand-primary-dark);
  }
}

details.payment-info-section {
  summary::-webkit-details-marker {
    display: none;
  }

  &[open] summary span:last-child {
    transform: rotate(90deg);
    display: inline-block;
  }
}
</style>

